import React, { useMemo } from "react";
import {
  isGoalserveTennisPlayerImage,
  resolveTennisPlayerImageS3UrlsFromName,
  splitTennisDoublesPlayerNames,
} from "../../../utils/tennisPlayerImageResolve";
import { useResolvedImageSrc } from "../../../utils/useResolvedImageSrc";

const parsePx = (value, fallback = 40) => {
  const match = String(value || "").match(/^([\d.]+)px$/i);
  return match ? parseFloat(match[1]) : fallback;
};

/**
 * Single tennis player headshot — S3 photo(s) keyed by player name slug, then Goalserve base64 from API,
 * then generic placeholder.
 *
 * Prefers a session-cached URL (or the first candidate optimistically), then confirms
 * off-DOM so missing assets never flash the browser broken-image icon or the placeholder.
 */
const TennisPlayerImageSingle = ({
  playerName = "",
  imageUrl = "",
  height = "40px",
  width = "40px",
  backdropColor = "",
  imgMixBlendMode = "",
  className = "",
}) => {
  const safePlayer = (playerName || "").toString().trim();
  const fallbackImageUrl = `${process.env.CDN_ENDPOINT}/player-placeholder-dark.svg`;
  const goalserveImage = isGoalserveTennisPlayerImage(imageUrl) ? imageUrl.trim() : null;
  const s3Urls = useMemo(
    () => resolveTennisPlayerImageS3UrlsFromName(safePlayer),
    [safePlayer],
  );
  const candidateUrls = useMemo(() => {
    const urls = [...s3Urls];
    if (goalserveImage && !urls.includes(goalserveImage)) {
      urls.push(goalserveImage);
    }
    return urls;
  }, [s3Urls, goalserveImage]);

  const { src, hidden, onError } = useResolvedImageSrc(candidateUrls, fallbackImageUrl);
  const isBase64 = Boolean(goalserveImage && src === goalserveImage);

  const altText = safePlayer ? `${safePlayer}` : "Player";
  const backdrop = String(backdropColor || "").trim();
  const blend = String(imgMixBlendMode || "").trim();
  const bullionTile = Boolean(backdrop || blend);

  return (
    <div
      className={`flex shrink-0 items-center justify-center overflow-hidden ${className}`.trim()}
      style={{
        width,
        height,
        minWidth: width,
        minHeight: height,
        ...(backdrop ? { backgroundColor: backdrop, isolation: "isolate" } : {}),
      }}
    >
      <img
        src={src}
        onError={onError}
        alt={altText}
        style={{
          width: "100%",
          height: "100%",
          maxWidth: "100%",
          maxHeight: "100%",
          objectFit: "contain",
          display: "block",
          ...(bullionTile
            ? {
                backgroundColor: "transparent",
                ...(blend ? { mixBlendMode: blend } : {}),
              }
            : {}),
          ...(hidden ? { visibility: "hidden" } : {}),
        }}
        loading={isBase64 ? "eager" : undefined}
        decoding="async"
      />
    </div>
  );
};

/**
 * Tennis player headshot — shows two images side-by-side for doubles pairs (e.g. "A/B").
 * Prefer backend-enriched `doublesPartners` (full names + Goalserve images from rankings).
 * Fall back to splitting the label — surname-only halves will miss S3 (no bare-surname keys)
 * and show the placeholder until enrichment supplies full names.
 */
export const TennisPlayerImage = ({
  playerName = "",
  imageUrl = "",
  doublesPartners,
  height = "40px",
  width = "40px",
  backdropColor = "",
  imgMixBlendMode = "",
  className = "",
  gapPx = 2,
}) => {
  const safePlayer = (playerName || "").toString().trim();

  const resolvedPartners = useMemo(() => {
    if (Array.isArray(doublesPartners) && doublesPartners.length === 2) {
      return doublesPartners
        .map((partner) => ({
          name: String(partner?.name || "").trim(),
          imageUrl: partner?.image && isGoalserveTennisPlayerImage(partner.image) ? partner.image : "",
        }))
        .filter((partner) => partner.name);
    }
    return splitTennisDoublesPlayerNames(safePlayer).map((name, index) => ({
      name,
      imageUrl: index === 0 && isGoalserveTennisPlayerImage(imageUrl) ? imageUrl : "",
    }));
  }, [doublesPartners, imageUrl, safePlayer]);

  const isDoubles = resolvedPartners.length === 2;

  if (!isDoubles) {
    return (
      <TennisPlayerImageSingle
        playerName={safePlayer}
        imageUrl={imageUrl}
        height={height}
        width={width}
        backdropColor={backdropColor}
        imgMixBlendMode={imgMixBlendMode}
        className={className}
      />
    );
  }

  const totalWidthPx = parsePx(width);
  const totalHeightPx = parsePx(height);
  const eachWidthPx = Math.max(1, (totalWidthPx - gapPx) / 2);
  const eachWidth = `${eachWidthPx}px`;
  const eachHeight = `${totalHeightPx}px`;

  return (
    <div
      className={`flex shrink-0 items-center justify-center overflow-hidden ${className}`.trim()}
      style={{
        width,
        height,
        minWidth: width,
        minHeight: height,
        gap: `${gapPx}px`,
      }}
      aria-label={safePlayer || "Doubles team"}
    >
      {resolvedPartners.map((partner, index) => (
        <TennisPlayerImageSingle
          key={`${partner.name}-${index}`}
          playerName={partner.name}
          imageUrl={partner.imageUrl || ""}
          height={eachHeight}
          width={eachWidth}
          backdropColor={backdropColor}
          imgMixBlendMode={imgMixBlendMode}
        />
      ))}
    </div>
  );
};

export default TennisPlayerImage;
